/**
 * crashMarker — names a heavy client-side step in `sessionStorage` before it
 * runs, so a renderer crash (an out-of-memory kill, a hung WASM call) that
 * never reaches an error boundary or a `window.onerror` handler still leaves
 * a trace: the next page load finds the marker still set and reports it.
 *
 * Every read/write is wrapped in try/catch. `sessionStorage` throws in some
 * browsers' private mode and is absent during SSR/tests, and a marker that
 * crashes the app it exists to protect would defeat its own purpose — the
 * app must work identically with storage unavailable.
 */

const STORAGE_KEY = 'dfq:crash-marker';

/**
 * Settings for the marker, not literals scattered at call sites. `maxFileTypeLength`
 * mirrors the platform's `file_type` field cap on `ReportClientErrorRequest`.
 */
export const CRASH_MARKER_SETTINGS = {
  maxFileTypeLength: 128,
} as const;

/**
 * Canonical names for the heavy steps this ticket wires up. Exported so every
 * call site (and the reporter that reads the marker back) shares one literal —
 * a typo in either place would silently break the "same step name" contract.
 */
export const CRASH_STEPS = {
  contourTraceOffset: 'contour_trace_offset',
  artworkDecodeWorker: 'artwork_decode_worker',
} as const;

export type CrashStep = (typeof CRASH_STEPS)[keyof typeof CRASH_STEPS];

export interface CrashMarkerContext {
  /** File size in bytes, when the step is processing a known file. */
  fileSizeBytes?: number;
  /** MIME type or detected file kind, when known. */
  fileType?: string;
  /** Product the artwork/shape belongs to, when known. */
  productId?: string | number;
}

export interface CrashMarkerRecord {
  step: string;
  startedAt: string;
  context?: CrashMarkerContext;
}

function getSessionStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function sanitizeContext(context: CrashMarkerContext): CrashMarkerContext | undefined {
  const sanitized: CrashMarkerContext = {};

  if (typeof context.fileSizeBytes === 'number' && Number.isFinite(context.fileSizeBytes)) {
    sanitized.fileSizeBytes = context.fileSizeBytes;
  }
  if (typeof context.fileType === 'string' && context.fileType !== '') {
    sanitized.fileType = context.fileType.slice(0, CRASH_MARKER_SETTINGS.maxFileTypeLength);
  }
  if (context.productId !== undefined) {
    sanitized.productId = context.productId;
  }

  return Object.keys(sanitized).length > 0 ? sanitized : undefined;
}

/**
 * Marks a heavy step as started. Call immediately before the risky work, not
 * inside a `try` around it — the marker is only useful if it lands before the
 * crash, and a wrapping try/catch here would swallow that ordering guarantee.
 */
export function markCrashStep(step: CrashStep | string, context?: CrashMarkerContext): void {
  try {
    const storage = getSessionStorage();
    if (!storage) return;

    const record: CrashMarkerRecord = {
      step,
      startedAt: new Date().toISOString(),
      ...(context ? { context: sanitizeContext(context) } : {}),
    };

    storage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Storage unavailable (private mode, quota, disabled) — nothing to do.
  }
}

/** Clears the marker on success. Call from both the happy path and `catch` — a caught, handled failure is not the crash this ticket reports. */
export function clearCrashStep(): void {
  try {
    getSessionStorage()?.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to clean up.
  }
}

/**
 * Reads and clears any marker left by a step that never completed. Always
 * clears, whether or not a marker was found — a crash report only makes
 * sense once, on the next load.
 */
export function consumeCrashMarker(): CrashMarkerRecord | null {
  try {
    const storage = getSessionStorage();
    if (!storage) return null;

    const raw = storage.getItem(STORAGE_KEY);
    storage.removeItem(STORAGE_KEY);

    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof (parsed as { step?: unknown }).step !== 'string'
    ) {
      return null;
    }

    return parsed as CrashMarkerRecord;
  } catch {
    return null;
  }
}
